import * as React from 'react';
import dateStyle from '@frontend/app/components/DateFilter/DateMenu.scss';
import { format } from 'date-fns';
import dayjs from 'dayjs';
import { DatePicker } from 'antd-v5';

import { Menu,
} from '@revfluence/fresh';
import { getCustomCompareRange } from '@frontend/app/containers/HomePageMetrics/components/DateSelect';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@frontend/shadcn/components/ui/select';
import styles from './DateFilter.scss';

function getCustomDateRangePicker(dateRange: [Date?, Date?]): [dayjs.Dayjs, dayjs.Dayjs] | null {
  if (!dateRange[0] || !dateRange[1]) return null;
  return [dayjs(dateRange[0]), dayjs(dateRange[1])];
}

export interface DateFilterOption {
  label: string;
  value: {
    dateRange: [Date?, Date?],
    compareRange: [Date?, Date?]
  };
}

export type DateFilterProps = {
  clientStartDate?: Date;
  defaultLabel?: string;
  options: DateFilterOption[];
  onFilterChange?: (options: DateFilterOption[]) => void;
  isCheckbox: boolean;
  dateRange?: [Date?, Date?];
  isCustomDate: boolean;
  refreshUi?: boolean;
  textColor?: string;
};

interface DateFilterOptions {
  options: DateFilterOption[];
  onChange: (option: DateFilterOption, checked: boolean) => void;
  clientStartDate?: Date;
  dateRange?: [Date?, Date?];
  isCustomDate: boolean;
}

const { useState, useEffect } = React;
function DateFilterDropdown({
  options, onChange, clientStartDate,
  dateRange, isCustomDate, refreshUi = true,
  selectedValue,
  textColor = 'text-primary-foreground',
}: DateFilterOptions & { refreshUi?: boolean; selectedValue: string; textColor?: string }) {
  const [open, setOpen] = useState(false);

  const onChangeCustomRange = React.useCallback((range) => {
    if (!range || !range[0] || !range[1]) return;

    const customStartDate = range[0].toDate();
    const customEndDate = range[1].toDate();
    const customDateOption: DateFilterOption = {
      label: `${format(customStartDate, 'MMM dd, yyyy')} - ${format(customEndDate, 'MMM dd, yyyy')}`,
      value: {
        dateRange: [customStartDate, customEndDate],
        compareRange: getCustomCompareRange(clientStartDate, [customStartDate, customEndDate]),
      },
    };
    onChange(customDateOption, true);
    setOpen(false);
  }, [onChange, clientStartDate]);

  const customPickerValue = isCustomDate ? getCustomDateRangePicker(dateRange) : null;
  const disabledDate = (currentDate: dayjs.Dayjs) => currentDate.isAfter(dayjs());

  if (refreshUi) {
    return (
      <Select
        value={selectedValue}
        onValueChange={(label) => {
          const selected = options.find((opt) => opt.label === label);
          if (selected) onChange(selected, true);
        }}
      >
        <SelectTrigger className={textColor}>
          <SelectValue>{selectedValue}</SelectValue>
        </SelectTrigger>
        <SelectContent className="z-[9999]">
          {options.map((option, index) => (
            <SelectItem key={index} value={option.label}>
              {option.label}
            </SelectItem>
          ))}
          <div className="px-2 py-2 border-t border-gray-200">
            <DatePicker.RangePicker
              disabledDate={disabledDate}
              allowClear
              open={open}
              onChange={onChangeCustomRange}
              onOpenChange={(open) => setOpen(open)}
              value={customPickerValue}
            />
          </div>
        </SelectContent>
      </Select>
    );
  }

  // Fallback to original Dropdown/Menu implementation
  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Menu className={styles.ProjectDropdown}>
        {options.map((option, index) => {
          const { label } = option;
          return (
            <Menu.Item key={index}>
              <div
                key={label}
                onClick={() => {
                  onChange(option, true);
                }}
              >
                {label}
              </div>
            </Menu.Item>
          );
        })}
        <Menu.Item className={dateStyle.customRangeDatePicker} key="Custom">
          <div className={dateStyle.customRangeWrapper} onClick={(e) => e.stopPropagation()}>
            <DatePicker.RangePicker
              disabledDate={disabledDate}
              allowClear
              open={open}
              onChange={onChangeCustomRange}
              onOpenChange={(open) => setOpen(open)}
              value={customPickerValue}
            />
          </div>
        </Menu.Item>
      </Menu>
    </div>
  );
}

function DateFilter({
  options,
  clientStartDate,
  defaultLabel = 'All Data',
  onFilterChange,
  dateRange,
  isCustomDate,
  refreshUi,
  textColor = 'text-primary-foreground',
}: DateFilterProps) {
  const [label, setLabel] = useState(defaultLabel);
  const [selection, setSelection] = useState<DateFilterOption[]>([]);

  const onSelectionChange = (option: DateFilterOption, checked: boolean) => {
    if (checked) {
      setSelection([option]);
      setLabel(option.label);
    } else {
      setSelection(selection.filter((opt) => option.label !== opt.label));
      setLabel(defaultLabel);
    }
  };

  useEffect(() => {
    if (typeof onFilterChange === 'function') {
      onFilterChange(selection);
    }
  }, [selection, onFilterChange]);

  return (
    <DateFilterDropdown
      options={options}
      onChange={onSelectionChange}
      clientStartDate={clientStartDate}
      dateRange={dateRange}
      isCustomDate={isCustomDate}
      refreshUi={refreshUi}
      selectedValue={label}
      textColor={textColor}
    />
  );
}

export default DateFilter;
